import {FC, useEffect, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import Table from '../../common/Table'
import TableHeader from '../../common/TableHeader'
import {useNavigate} from 'react-router-dom'
import {useDispatch, useSelector} from 'react-redux'
import {AppDispatch, RootState} from '../../../../../redux/store'
import {getClientListApi, deleteClientApi} from '../../../../../redux/slices/ClientSlice'
import {toast} from 'react-toastify'
import KYCDocumentModal from './KYCDocumentModal'
import ClientBanksModal from './ClientBanksModal'

const ClientTable: FC = () => {
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(100)
  const [searchKey, setSearchKey] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [selectedRow, setSelectedRow] = useState<any[]>([])
  const [showKYCModal, setShowKYCModal] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null)
  const [showBankModal, setShowBankModal] = useState(false)
  const [bankModalLeadId, setBankModalLeadId] = useState<number | null>(null)
  const [bankModalClientName, setBankModalClientName] = useState('')
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  const clientData = useSelector((state: RootState) => state.client.ClientList?.data?.data || [])
  const clientTotal = useSelector((state: RootState) => state.client.ClientList?.data?.total || 0)

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setDebouncedSearchQuery(searchKey)
    }, 1000)

    return () => clearTimeout(debounceTimeout)
  }, [searchKey])

  const fetchClientList = () => {
    setLoading(true)
    dispatch(
      getClientListApi({
        pageNumber: currentPage,
        pageSize: rowsPerPage,
        filterkey: debouncedSearchQuery,
      })
    )
      .then(() => {
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        toast.error('Failed to fetch client data')
      })
  }

  useEffect(() => {
    fetchClientList()
  }, [currentPage, rowsPerPage, debouncedSearchQuery])

  const handleEdit = (id: any) => {
    // navigate(`edit/${id}`)
    window.open(`/portal#/crm/client/edit/${id}`, '_blank')
  }
  const handleClientLog = (id: any) => {
    navigate(`client-log/${id}`)
  }
  const handleView = async (id: any) => {
    try {
      const response: any = await dispatch(
        getClientListApi({
          pageNumber: currentPage,
          pageSize: rowsPerPage,
          filterkey: '',
        })
      )

      const selectedClient = response.payload?.data?.data?.find((client: any) => client.id === id)
      const profile_image_url = selectedClient?.profile_image_url || selectedClient?.profile_image

      if (profile_image_url) {
        const newWindow = window.open('', '_blank')
        newWindow?.document.write(`
          <html>
            <head>
              <title>Profile Image</title>
            </head>
            <body style="text-align: center; margin: 0; padding: 0; display: flex; justify-content: center; align-items: center; height: 100vh;">
              <img src="${profile_image_url}" alt="Profile Image" style="max-width: 80%; max-height: 80%; border: 1px solid #ccc;"/>
            </body>
          </html>
        `)
      } else {
        toast.error('Image not found')
      }
    } catch (error) {
      toast.error('Error fetching client data')
    }
  }

  const handleDelete = (id: any) => {
    dispatch(deleteClientApi(id)).then((response: any) => {
      if (response.payload.success) {
        toast.success(response.payload.reason)
        fetchClientList()
      } else {
        toast.error(response.payload.reason || 'Something went wrong')
      }
    })
  }

  const handleSearchChange = (searchQuery: string) => {
    setSearchKey(searchQuery)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setCurrentPage(page)
    setRowsPerPage(newPerPage)
  }

  // KYC Modal handlers
  const handleKYCView = (leadId: number) => {
    setSelectedLeadId(leadId)
    setShowKYCModal(true)
  }

  const handleKYCModalClose = () => {
    setShowKYCModal(false)
    setSelectedLeadId(null)
  }

  const handleKYCStatusUpdate = () => {
    // Refresh the client list after KYC status update
    fetchClientList()
  }

  const handleOpenBanks = (id: number, name?: string) => {
    setBankModalLeadId(id)
    setBankModalClientName(name || '')
    setShowBankModal(true)
  }

  const handleBankModalClose = () => {
    setShowBankModal(false)
    setBankModalLeadId(null)
    setBankModalClientName('')
  }

  const columnData = [
    {
      name: 'Client',
      cell: (row: any) => {
        const profileUrl = row?.profile_image_url
        return (
          <div className='d-flex align-items-center py-3'>
            <div className='symbol symbol-50px me-3'>
              {profileUrl ? (
                <img
                  src={profileUrl}
                  alt={row?.name}
                  style={{borderRadius: '50%', objectFit: 'cover', width: '50px', height: '50px'}}
                />
              ) : (
                <div
                  className='symbol-label bg-light-primary'
                  style={{
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '50px',
                    height: '50px',
                  }}
                >
                  <i className='fas fa-user fs-2 text-primary' />
                </div>
              )}
            </div>
            <div className='d-flex flex-column'>
              <span className='text-dark fw-bold fs-6 mb-1'>{row?.name || '-'}</span>
              <span className='text-muted fw-semibold fs-7'>Client ID: {row?.id || '-'}</span>
            </div>
          </div>
        )
      },
      minWidth: '250px',
      sortable: true,
    },
    {
      name: 'Basic Details',
      cell: (row: any) => {
        const roleId = row?.role_id
        const roleName = roleId === 1 ? 'Dealer' : roleId === 2 ? 'Carpenter' : '-'
        return (
          <div className='d-flex flex-column py-2'>
            <div className='mb-1'>
              <i className='bi bi-briefcase-fill text-primary me-2' />
              <span className='text-dark fw-semibold fs-7'>Role: {roleName}</span>
            </div>
            <div>
              <i className='bi bi-person-badge text-muted me-2' />
              <span className='text-muted fw-semibold fs-7'>Role ID: {row?.role_id || '-'}</span>
            </div>
          </div>
        )
      },
      minWidth: '180px',
      sortable: true,
    },
    {
      name: 'Contact',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          <div className='mb-1'>
            <i className='bi bi-telephone-fill text-primary me-2' />
            <span className='text-dark fw-semibold fs-7'>{row?.phone || '-'}</span>
          </div>
          <div className='mb-1'>
            <i className='bi bi-envelope-fill text-muted me-2' />
            <span className='text-muted fw-semibold fs-7' style={{fontSize: '0.8rem'}}>
              {row?.email || '-'}
            </span>
          </div>
          <div>
            <i className='bi bi-geo-alt-fill text-muted me-2' />
            <span className='text-muted fw-semibold fs-7'>
              {row?.address
                ? row.address.length > 15
                  ? row.address.substring(0, 15) + '...'
                  : row.address
                : '-'}
            </span>
          </div>
        </div>
      ),
      minWidth: '220px',
      sortable: true,
    },
    {
      name: 'Wallet',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          <div className='mb-1'>
            <i className='bi bi-currency-rupee text-primary me-2' />
            <span className='text-dark fw-semibold fs-7'>
              {row?.wallet_money ? Number(row.wallet_money).toFixed(2) : '0.00'}
            </span>
          </div>
          <div>
            <i className='bi bi-wallet2 text-muted me-2' />
            <span className='text-muted fw-semibold fs-7'>{row?.wallet_points || 0}</span>
          </div>
        </div>
      ),
      minWidth: '150px',
      sortable: true,
    },
    {
      name: 'Date & Time',
      cell: (row: any) => (
        <div className='d-flex flex-column py-2'>
          <div className='mb-1 d-flex align-items-center'>
            <i className='bi bi-calendar-check-fill text-primary me-2' />
            <span className='text-dark fw-semibold fs-7'>
              {row?.created_at
                ? new Date(row.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '-'}
            </span>
          </div>
          <div className='d-flex align-items-center'>
            <i className='bi bi-clock-fill text-muted me-2' />
            <span className='text-muted fw-semibold fs-7'>
              {row?.created_at
                ? new Date(row.created_at).toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true,
                  })
                : '-'}
            </span>
          </div>
        </div>
      ),
      minWidth: '160px',
      sortable: true,
    },
    {
      name: 'KYC Action',
      cell: (row: any) => {
        const kycStatus = row.kyc_status || row.status || 'no_kyc'
        const hasKYC = row.kyc_doc_file_url || row.kyc_doc_type || row.kyc_doc_file
        const isPending = kycStatus?.toLowerCase() === 'pending'

        return (
          <div className='d-flex flex-column align-items-start py-2' style={{gap: '8px'}}>
            {hasKYC ? (
              <OverlayTrigger
                placement='top'
                overlay={<Tooltip>Click to view KYC document</Tooltip>}
              >
                <span
                  className={`badge ${
                    kycStatus === 'approved'
                      ? 'badge-success'
                      : kycStatus === 'rejected'
                      ? 'badge-danger'
                      : kycStatus === 'pending'
                      ? 'badge-warning'
                      : 'badge-secondary'
                  } fw-bold`}
                  onClick={() => handleKYCView(row?.id)}
                  style={{
                    cursor: 'pointer',
                    padding: '8px 16px',
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    minWidth: '100px',
                    textAlign: 'center',
                  }}
                >
                  {kycStatus === 'approved'
                    ? 'APPROVED'
                    : kycStatus === 'rejected'
                    ? 'REJECTED'
                    : kycStatus === 'pending'
                    ? 'PENDING'
                    : 'NO KYC'}
                </span>
              </OverlayTrigger>
            ) : (
              <span
                className='badge badge-secondary fw-bold'
                style={{
                  padding: '8px 16px',
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                  minWidth: '100px',
                  textAlign: 'center',
                }}
              >
                NO KYC
              </span>
            )}

            {isPending && hasKYC && (
              <div className='d-flex gap-2'>
                <OverlayTrigger placement='top' overlay={<Tooltip>Approve KYC</Tooltip>}>
                  <button
                    className='btn btn-sm'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleKYCView(row?.id)
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#50cd89',
                      padding: '4px',
                    }}
                  >
                    <i className='bi bi-check-circle-fill fs-4'></i>
                  </button>
                </OverlayTrigger>

                <OverlayTrigger placement='top' overlay={<Tooltip>Reject KYC</Tooltip>}>
                  <button
                    className='btn btn-sm'
                    onClick={(e) => {
                      e.stopPropagation()
                      handleKYCView(row?.id)
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#f1416c',
                      padding: '4px',
                    }}
                  >
                    <i className='bi bi-x-circle-fill fs-4'></i>
                  </button>
                </OverlayTrigger>
              </div>
            )}
          </div>
        )
      },
      minWidth: '150px',
      sortable: false,
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <div className='d-flex align-items-center gap-2 py-2'>
          <OverlayTrigger placement='top' overlay={<Tooltip>Bank accounts</Tooltip>}>
            <button
              type='button'
              className='btn btn-icon btn-light-primary btn-sm'
              onClick={() => handleOpenBanks(row?.id, row?.name)}
              style={{
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
              }}
            >
              <i className='bi bi-bank fs-5'></i>
            </button>
          </OverlayTrigger>

          <OverlayTrigger placement='top' overlay={<Tooltip>View Profile</Tooltip>}>
            <button
              className='btn btn-icon btn-light-primary btn-sm'
              onClick={() => handleView(row?.id)}
              style={{
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
              }}
            >
              <i className='bi bi-eye fs-5'></i>
            </button>
          </OverlayTrigger>

          <OverlayTrigger placement='top' overlay={<Tooltip>Edit Client</Tooltip>}>
            <button
              className='btn btn-icon btn-light-primary btn-sm'
              onClick={() => handleEdit(row?.id)}
              style={{
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
              }}
            >
              <i className='bi bi-pencil fs-5'></i>
            </button>
          </OverlayTrigger>

          <OverlayTrigger placement='top' overlay={<Tooltip>Client Logs</Tooltip>}>
            <button
              className='btn btn-icon btn-light-primary btn-sm'
              onClick={() => handleClientLog(row?.id)}
              style={{
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
              }}
            >
              <i className='bi bi-journal-text fs-5'></i>
            </button>
          </OverlayTrigger>

          <OverlayTrigger placement='top' overlay={<Tooltip>Delete Client</Tooltip>}>
            <button
              className='btn btn-icon btn-light-danger btn-sm'
              onClick={() => handleDelete(row?.id)}
              style={{
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '6px',
              }}
            >
              <i className='bi bi-trash fs-5'></i>
            </button>
          </OverlayTrigger>
        </div>
      ),
      minWidth: '250px',
      sortable: false,
    },
  ]

  return (
    <>
      <div className='card shadow-sm'>
        <TableHeader
          search={searchKey}
          addButtonText={'Add Client'}
          handleSearchChange={handleSearchChange}
          handleAddButton={() => navigate('add')}
          showFilter={true}
          filterContent={<p>Custom filter form content here</p>}
        />

        <div className='card-body p-0 shadow-sm'>
          <Table
            data={clientData}
            columns={columnData}
            isLoading={loading}
            handlePerRowsChange={handlePerRowsChange}
            handlePageChange={handlePageChange}
            totalRows={clientTotal}
            perPageData={rowsPerPage}
            showPagination={true}
            onRowClicked={(row: any) => {}}
            selectableRows
            onSelectedRowsChange={(selected: any) => {
              setSelectedRow(selected.selectedRows)
            }}
          />
        </div>
      </div>

      {/* KYC Document Modal */}
      <KYCDocumentModal
        show={showKYCModal}
        onHide={handleKYCModalClose}
        leadId={selectedLeadId}
        onStatusUpdate={handleKYCStatusUpdate}
      />

      <ClientBanksModal
        show={showBankModal}
        onHide={handleBankModalClose}
        leadId={bankModalLeadId}
        clientName={bankModalClientName}
      />
    </>
  )
}

export default ClientTable
